import React, { useRef, useState, useEffect } from 'react';
import { EmptyState } from './shared';

const BLUE_GRAD = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const C_BORDER = 'rgba(0,0,0,0.06)';
const C_LABEL = '#aeaeb2';
const C_PRIMARY = '#1d1d1f';
const C_SECONDARY = '#6e6e73';
const C_BLUE = '#003585';
const C_BLUESOFT = 'rgba(0,53,133,0.07)';

const Card = ({ children, style = {}, isMobile = false }) => (
  <div style={{ 
    background: '#ffffff', 
    border: `1px solid ${C_BORDER}`, 
    borderRadius: isMobile ? 8 : 10, 
    padding: isMobile ? 10 : 14, 
    display: 'flex', 
    flexDirection: 'column',
    minWidth: 0,
    ...style 
  }}>
    {children}
  </div>
);

const Label = ({ children, light = false, isMobile = false }) => (
  <p style={{ 
    fontFamily: "'Sansation', sans-serif", 
    fontSize: isMobile ? 8 : 9, 
    fontWeight: 700, 
    letterSpacing: isMobile ? '0.06em' : '0.08em', 
    textTransform: 'uppercase', 
    color: light ? 'rgba(255,255,255,0.55)' : C_LABEL, 
    margin: '0 0 6px 0' 
  }}>
    {children}
  </p>
);

const Stat = ({ children, blue = false, light = false, isMobile = false, isTablet = false }) => (
  <p style={{ 
    fontFamily: "'Sansation', sans-serif", 
    fontSize: isMobile ? 14 : (isTablet ? 16 : 18), 
    fontWeight: 800, 
    color: light ? '#ffffff' : blue ? C_BLUE : C_PRIMARY, 
    lineHeight: 1.2, 
    margin: 0,
    wordBreak: 'break-word',
  }}>
    {children}
  </p>
);

const Value = ({ children, light = false, size = 14, isMobile = false, isTablet = false }) => (
  <p style={{ 
    fontFamily: "'Sansation', sans-serif", 
    fontSize: isMobile ? size - 2 : (isTablet ? size - 1 : size), 
    fontWeight: 700, 
    color: light ? 'rgba(255,255,255,0.85)' : C_PRIMARY, 
    lineHeight: 1.3, 
    margin: 0,
    wordBreak: 'break-word',
  }}>
    {children}
  </p>
);

const Sub = ({ children, light = false, style = {}, isMobile = false }) => (
  <p style={{ 
    fontFamily: 'inherit', 
    fontSize: isMobile ? 11 : 12.5, 
    color: light ? 'rgba(255,255,255,0.85)' : C_SECONDARY, 
    lineHeight: 1.5, 
    margin: '2px 0 0',
    wordBreak: 'break-word',
    ...style 
  }}>
    {children}
  </p>
);

const PitchCanvas = ({ pitchCanvasData, startupData }) => {
  const canvasRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  console.log(pitchCanvasData);

  const byLabel = (kw) => {
    const kws = Array.isArray(kw) ? kw : [kw];
    return pitchCanvasData.find(c => kws.some(k => c.label.toLowerCase().includes(k.toLowerCase())));
  };

  const businessModel = byLabel(['business model']);
  const foundedIn = byLabel(['founded in', 'founded year', 'founded']);
  const industry = byLabel(['industry']);
  const fundingStage = byLabel(['funding stage']);
  const valueProp = byLabel(['value prop', 'value proposition']);
  const problem = byLabel(['problem']);
  const solution = byLabel(['solution']);
  const revenue = byLabel(['revenue', 'arr']);
  const fundingAsk = byLabel(['funding ask', 'funding target']);
  const traction = byLabel(['traction']);
  const pipeline = byLabel(['pipeline']);
  const foundingTeam = byLabel(['founding team', 'team']);
  const customerSeg = byLabel(['customer segment']);
  const aboutCompany = byLabel(['about']);
  const competitors = byLabel(['competitor', 'competition']);

  const handleDownload = async () => {
    const node = canvasRef.current;
    if (!node) return;

    // Dynamically load html2canvas + jsPDF from CDN
    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    try {
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js');
      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');

      const canvas = await window.html2canvas(node, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f8f9fb',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const { jsPDF } = window.jspdf;
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width / 2, canvas.height / 2],
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width / 2, canvas.height / 2);
      const companyName = startupData?.company_name || 'startup';
      pdf.save(`${companyName.replace(/\s+/g, '_')}_pitch_canvas.pdf`);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        justifyContent: 'space-between', 
        marginBottom: isMobile ? 14 : 20,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 10 : 0,
      }}>
        <h2 style={{ 
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 800, 
          letterSpacing: '-0.02em', 
          color: C_PRIMARY, 
          margin: 0 
        }}>
          myPitch Canvas
        </h2>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 8 : 10,
          flexWrap: 'wrap',
        }}>
          <span style={{ 
            fontSize: isMobile ? 9 : 10, 
            fontWeight: 600, 
            padding: isMobile ? '3px 8px' : '3px 10px', 
            borderRadius: 6, 
            background: C_BLUESOFT, 
            color: C_BLUE,
            whiteSpace: 'nowrap',
          }}>
            Startup Profile
          </span>
          {pitchCanvasData.length > 0 && (
            <button
              onClick={handleDownload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: isMobile ? 4 : 6,
                padding: isMobile ? '5px 10px' : '6px 14px',
                borderRadius: isMobile ? 6 : 8,
                border: '1.5px solid rgba(0,53,133,0.18)',
                background: '#ffffff',
                color: C_BLUE,
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                fontFamily: "'Sansation', sans-serif",
                cursor: 'pointer',
                letterSpacing: '0.01em',
                transition: 'all 0.15s',
                boxShadow: '0 1px 4px rgba(0,53,133,0.08)',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = C_BLUESOFT;
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,53,133,0.15)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,53,133,0.08)';
              }}
            >
              <svg width={isMobile ? 11 : 13} height={isMobile ? 11 : 13} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1v9M4.5 6.5L8 10l3.5-3.5" stroke={C_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12h12" stroke={C_BLUE} strokeWidth="1.8" strokeLinecap="round"/>
                <path d="M2 12v2h12v-2" stroke={C_BLUE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {!isMobile && 'Download PDF'}
              {isMobile && 'PDF'}
            </button>
          )}
        </div>
      </div>

      <div ref={canvasRef} style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 8 : 10, 
        background: '#f8f9fb', 
        padding: 2, 
        borderRadius: isMobile ? 10 : 12,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}>
        {pitchCanvasData.length > 0 ? (
          <>
            {/* ROW 1 */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(4,1fr)'), 
              gap: isMobile ? 8 : 10, 
              alignItems: 'stretch' 
            }}>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{businessModel?.label || 'Business Model'}</Label>
                <Value size={14} isMobile={isMobile} isTablet={isTablet}>{businessModel?.value || startupData?.business_model || '—'}</Value>
                <Sub isMobile={isMobile}>{startupData?.deck_type || ''}</Sub>
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{foundedIn?.label || 'Founded In'}</Label>
                <Stat isMobile={isMobile} isTablet={isTablet}>{foundedIn?.value || startupData?.founded_year || '—'}</Stat>
              </Card>
              <Card style={{ background: BLUE_GRAD, border: 'none' }} isMobile={isMobile}>
                <Label light isMobile={isMobile}>{industry?.label || 'Industry'}</Label>
                <Value light size={14} isMobile={isMobile} isTablet={isTablet}>{industry?.value || startupData?.industry || '—'}</Value>
                {(industry?.subtext || startupData?.deck_type) && <Sub style={{ marginTop: 2, color: 'rgba(255,255,255,0.7)' }} isMobile={isMobile}>{industry?.subtext || startupData?.deck_type}</Sub>}
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{fundingStage?.label || 'Funding Stage'}</Label>
                <Stat isMobile={isMobile} isTablet={isTablet}>{fundingStage?.value || startupData?.funding_stage || '—'}</Stat>
              </Card>
            </div>

            {/* ROW 2 — Value Prop */}
            <Card style={{ background: BLUE_GRAD, border: 'none' }} isMobile={isMobile}>
              <Label light isMobile={isMobile}>{valueProp?.label || 'Value Proposition'}</Label>
              <p style={{ 
                fontFamily: 'inherit', 
                fontSize: isMobile ? 12 : (isTablet ? 13 : 13.5), 
                fontWeight: 500, 
                color: 'rgba(255,255,255,0.85)', 
                lineHeight: 1.6, 
                margin: 0,
                wordBreak: 'break-word',
              }}>
                {valueProp?.value || startupData?.value_proposition || '—'}
              </p>
            </Card>

            {/* ROW 3 — Problem | Solution */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: isMobile ? 8 : 10 
            }}>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{problem?.label || 'Problem'}</Label>
                <Sub isMobile={isMobile}>{problem?.value || '—'}</Sub>
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{solution?.label || 'Solution'}</Label>
                <Sub isMobile={isMobile}>{solution?.value || '—'}</Sub>
              </Card>
            </div>

            {/* ROW 4 — Metrics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : (isTablet ? 'repeat(2, 1fr)' : 'repeat(4,1fr)'), 
              gap: isMobile ? 8 : 10 
            }}>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{revenue?.label || 'Revenue'}</Label>
                <Stat isMobile={isMobile} isTablet={isTablet}>{revenue?.value || '—'}</Stat>
                <Sub isMobile={isMobile}>ARR · 100% MoM</Sub>
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{fundingAsk?.label || 'Funding Ask'}</Label>
                <Stat blue isMobile={isMobile} isTablet={isTablet}>{fundingAsk?.value || startupData?.funding_target || '—'}</Stat>
                <Sub isMobile={isMobile}>Scale GTM + product</Sub>
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{traction?.label || 'Traction'}</Label>
                <Stat isMobile={isMobile} isTablet={isTablet}>{traction?.value || '—'}</Stat>
                <Sub isMobile={isMobile}>Autonomous resolution</Sub>
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{pipeline?.label || 'Pipeline'}</Label>
                <Stat isMobile={isMobile} isTablet={isTablet}>{pipeline?.value || '—'}</Stat>
                <Sub isMobile={isMobile}>Qualified pipeline</Sub>
              </Card>
            </div>

            {/* ROW 5 — Team | Customers */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: isMobile ? 8 : 10 
            }}>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{foundingTeam?.label || 'Founding Team'}</Label>
                {foundingTeam ? (
                  <>
                    <Value size={13} isMobile={isMobile} isTablet={isTablet}>{foundingTeam.value.split('·')[0]?.trim() || foundingTeam.value}</Value>
                    <Sub style={{ marginTop: 4 }} isMobile={isMobile}>{foundingTeam.value}</Sub>
                  </>
                ) : startupData?.founding_team ? (
                  <Sub isMobile={isMobile}>{startupData.founding_team}</Sub>
                ) : (
                  <Sub isMobile={isMobile}>—</Sub>
                )}
              </Card>
              <Card isMobile={isMobile}>
                <Label isMobile={isMobile}>{customerSeg?.label || 'Customer Segments'}</Label>
                {customerSeg ? (
                  <>
                    <Value size={13} isMobile={isMobile} isTablet={isTablet}>{customerSeg.value.split('\n')[0] || customerSeg.value}</Value>
                    {customerSeg.value.includes('\n') && <Sub style={{ marginTop: 4 }} isMobile={isMobile}>{customerSeg.value.split('\n').slice(1).join(' ')}</Sub>}
                  </>
                ) : startupData?.customer_segments ? (
                  <Sub isMobile={isMobile}>{startupData.customer_segments}</Sub>
                ) : (
                  <Sub isMobile={isMobile}>—</Sub>
                )}
              </Card>
            </div>

            {/* ROW 6 — About | Competitors */}
            {(aboutCompany || startupData?.about || competitors) && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : (isTablet ? '2fr 1fr' : '3fr 1fr'), 
                gap: isMobile ? 8 : 10 
              }}>
                <Card isMobile={isMobile}>
                  <Label isMobile={isMobile}>{aboutCompany?.label || 'About'}</Label>
                  <Sub isMobile={isMobile}>{aboutCompany?.value || startupData?.about || '—'}</Sub>
                </Card>
                <Card isMobile={isMobile}>
                  <Label isMobile={isMobile}>{competitors?.label || 'Competitors'}</Label>
                  <Sub isMobile={isMobile}>{competitors?.value || '—'}</Sub>
                </Card>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon="Layout" title="myPitch Canvas" desc="Startup profile data parsed from your HTML report will appear here." />
        )}
      </div>
    </div>
  );
};

export default PitchCanvas;